import { EnumFasesSacola } from "../../../cardapio-sacola";
import { Box, Button, Divider, Grid, LinearProgress, Slide, Typography, useTheme } from "@material-ui/core";
import { useStyles } from './pagamento-pix-styles'
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNotificacoes } from 'components';
import { PagamentoPixDetalhes } from './components/pagamento-pix/pagamento-pix-detalhes';
import { MovParcelaResponseModel } from 'api/cardapio/models/mov-pag/mov-registrar-response';
import { useGetMovPagamentoById } from 'api/cardapio/queries/mov-pag/get-mov-pagamento-by-id';
import classNames from "classnames";
import { useConfirm } from "material-ui-confirm";
import { useParams } from 'react-router-dom';
import { CancelarIcon } from "components/icons/cancelar-icon";
import { usePedidos } from "services/providers/pedidos-provider";

export interface PagamentoPixProps {
    faseSacola: EnumFasesSacola;
    fecharSacola: () => void;
    onClickProximaFase: (nextOrBack: React.MutableRefObject<0 | 1>, fase: EnumFasesSacola) => void
    onClickVoltarFase: (nextOrBack: React.MutableRefObject<0 | 1>, fase: EnumFasesSacola) => void
}

export enum StatusPagamento {
    Aberto,
    Pago,
    Cancelado
}

export enum EnumStatusPix {
    Aceito,
    Cancelado,
    Neutro
}

export const PagamentoPix = ({
    faseSacola,
    fecharSacola,
    onClickProximaFase,
    onClickVoltarFase,
}: PagamentoPixProps) => {
    const classes = useStyles()
    const theme = useTheme()
    const confirm = useConfirm()
    const fasePagamento = faseSacola === EnumFasesSacola.PagamentoPix
    const nextOrBack = useRef<0 | 1>(1)
    const tentativasPagamento = useRef<number>(1)

    const {
        dadosPix,
        carregandoHandlePedidoPix,
        cancelarPedido,
        setTransacao,
        isPedidoDelivery
    } = usePedidos()

    const { getMovPagamentoById } = useGetMovPagamentoById()
    const [idInterval, setIdInterval] = useState<NodeJS.Timeout>()
    const [cancelarInterval, setCancelarInterval] = useState<boolean>(false)
    const [progress, setProgress] = useState(0);
    const [statusPix, setStatusPix] = useState<EnumStatusPix>(EnumStatusPix.Neutro)
    const { showErrorMessage, showSuccessMessage } = useNotificacoes()
    const [pedidoErro, setPedidoErro] = useState<boolean>(false);
    const { empresaId } = useParams<{ empresaId: string }>();
    const [ocorreuErro, setOcorreuErro] = useState<boolean>(false);
    const tentativasErros = useRef<number>(0)

    const verificarPagamento = useCallback(async () => {
        const expiracao = new Date(dadosPix?.dataExpiracao ?? new Date())
        try {
            if (fasePagamento) {
                const res = await getMovPagamentoById(dadosPix?.id ?? '', empresaId, tentativasPagamento.current >= 3)
                if (res.erro) throw res.erro

                const data = res.resultado?.data

                if (data === StatusPagamento.Pago) {
                    setCancelarInterval(true)
                    setStatusPix(EnumStatusPix.Aceito)
                    onClickProximaFase(nextOrBack, EnumFasesSacola.FazendoPedido)
                    return
                } else if (new Date().getTime() >= expiracao.getTime()) {
                    setStatusPix(EnumStatusPix.Cancelado)
                    setCancelarInterval(true)
                    return
                }
                tentativasPagamento.current++
                setOcorreuErro(false)
                tentativasErros.current = 0
            }
        } catch (err: any) {
            setOcorreuErro(true)
            tentativasErros.current++
            if (tentativasErros.current >= 5) {
                setPedidoErro(err.message)
                setCancelarInterval(true)
            }
        }
    }, [dadosPix?.dataExpiracao, dadosPix?.id, empresaId, fasePagamento, getMovPagamentoById, onClickProximaFase])

    useEffect(() => {
        if (fasePagamento) {
            const intervalId = setInterval(verificarPagamento, 10000)
            setIdInterval(intervalId)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fasePagamento])

    useEffect(() => {
        if ((cancelarInterval && idInterval) || (idInterval && !fasePagamento)) {
            clearInterval(idInterval)
        }
    }, [cancelarInterval, fasePagamento, idInterval])

    useEffect(() => {
        if (!pedidoErro && fasePagamento) {
            const timer = setInterval(() => {
                setProgress((prevProgress) => (prevProgress >= 100 ? 10 : prevProgress + 10));
            }, 1000);
            return () => {
                clearInterval(timer);
            };
        }
    }, [fasePagamento, pedidoErro]);

    const handleCancelar = useCallback(() => {
        setCancelarInterval(true)
        cancelarPedido()
        showSuccessMessage('Pedido cancelado!')
        fecharSacola()
    }, [cancelarPedido, fecharSacola, showSuccessMessage])

    const clickPedido = useCallback(async () => {
        try {
            setCancelarInterval(true)
            setTransacao(undefined)
            onClickProximaFase(nextOrBack, EnumFasesSacola.FazendoPedido)
        } catch (err: any) {
            setPedidoErro(true)
            showErrorMessage(`Ocorreu um problema ao enviar seu pedido. Detalhe: ${err.message}`)
        }
    }, [onClickProximaFase, setTransacao, showErrorMessage])

    const clickPedidoPix = useCallback(async () => {
        try {
            setCancelarInterval(true)
            onClickProximaFase(nextOrBack, EnumFasesSacola.FazendoPedido)
        } catch (err: any) {
            setPedidoErro(true)
            showErrorMessage(`Ocorreu um problema ao enviar seu pedido. Detalhe: ${err.message}`)
        }
    }, [onClickProximaFase, showErrorMessage])

    const clickPedidoConfirm = useCallback(() => {
        confirm({
            title: 'Pagar depois',
            description: "Gostaria de pagar seu pedido depois?",
            confirmationText: "Sim",
            confirmationButtonProps: {
                variant: 'contained',
                color: 'primary',
                fullWidth: true
            },
            cancellationText: 'Não',
            cancellationButtonProps: {
                variant: 'outlined',
                color: theme.palette.type === 'dark' ? 'inherit' : 'primary',
                fullWidth: true
            }
        }).then(async () => {
            await clickPedido()
        })
    }, [clickPedido, confirm, theme.palette.type])

    const clickCancelarConfirm = useCallback(() => {
        confirm({
            title: 'Cancelar Pedido',
            description: "Gostaria de cancelar seu pedido?",
            confirmationText: "Sim",
            confirmationButtonProps: {
                variant: 'contained',
                color: 'primary',
                fullWidth: true
            },
            cancellationText: 'Não',
            cancellationButtonProps: {
                variant: 'outlined',
                color: theme.palette.type === 'dark' ? 'inherit' : 'primary',
                fullWidth: true
            }
        }).then(() => handleCancelar())
    }, [confirm, handleCancelar, theme.palette.type])

    return (
        <Slide direction={nextOrBack ? "left" : 'right'} mountOnEnter unmountOnExit in={fasePagamento}>
            <Box height={"100%"} width={"100%"} className={classes.boxContainer}>
                {statusPix === EnumStatusPix.Neutro && !carregandoHandlePedidoPix && !pedidoErro && (
                    <div className={classes.header}>
                        <Box textAlign={'center'} flexDirection="column">
                            <LinearProgress className={classes.progressBar} variant="determinate" color={"primary"} value={progress} />
                            <Typography variant="caption" color="textSecondary">A cada 10 segundos verificamos se o pagamento foi efetuado.</Typography>
                            <Typography variant="caption" color="textSecondary">{ocorreuErro ? 'Ocorreu um erro, tentando novamente' : 'Tudo certo até agora.'}</Typography>
                        </Box>
                    </div>
                )}
                <div className={classes.scrollable} style={{
                    height: '100%',
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'center',
                    alignItems: 'center',
                    overflowX: 'hidden'
                }}>
                    <PagamentoPixDetalhes
                        model={dadosPix ?? new MovParcelaResponseModel()}
                        carregandoCriarPedido={carregandoHandlePedidoPix}
                        statusPix={statusPix}
                        fechar={fecharSacola}
                        pedidoErro={pedidoErro}
                        handlePedido={clickPedido}
                        handlePedidoPix={clickPedidoPix}
                        cancelarPedido={handleCancelar}
                    />
                </div>

                {statusPix === EnumStatusPix.Neutro && !carregandoHandlePedidoPix && !pedidoErro && (
                    <div className={classes.footer}>
                        <Divider style={{ marginTop: '1rem' }} />
                        <Grid container justify="center" className={classes.grdBtns}>
                            {isPedidoDelivery ? (
                                <Button
                                    onClick={clickCancelarConfirm}
                                    color={theme.palette.type === "light" ? "primary" : "inherit"}
                                    variant="text"
                                    size="large"
                                    className={classNames(classes.btnFooter)}
                                    fullWidth
                                >
                                    <CancelarIcon tipo={theme.palette.type === "light" ? 'BUTTON' : 'BUTTON_PRIMARY'} />
                                    Cancelar Pedido
                                </Button>
                            ) : (
                                <Button
                                    onClick={clickPedidoConfirm}
                                    color={theme.palette.type === "light" ? "primary" : "inherit"}
                                    variant="text"
                                    size="large"
                                    className={classNames(classes.btnFooter)}
                                    fullWidth
                                >
                                    <CancelarIcon tipo={theme.palette.type === "light" ? 'BUTTON' : 'BUTTON_PRIMARY'} />
                                    Pagar depois
                                </Button>
                            )}
                        </Grid>
                    </div>
                )}
            </Box>
        </Slide>
    )
}